'use client';

import React, { useEffect } from 'react';
import useNotificationStore from 'src/stores/useNotificationStore';
import { notify } from 'src/utils/notifications';
import { BehaviorSubject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { VelocityWindow } from 'src/window/velocityWindow';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';
import useVelocityStore from 'src/stores/VelocityStore/useVelocityStore';
import { useTargetMarketSlotStaleness } from './useTargetMarketSlotStaleness';
import Button from 'src/components/primitives/Button/Button';
import { useForceSlotResubscribe } from 'src/hooks/useForceSlotResubscribe';
import { msToSlotsNum } from '@velocity-exchange/sdk';
import { useRiskCeilingSlotClock } from 'src/hooks/useSlotClock';

const NOTIFICATION_DEBOUNCE_TIME_MS = 2_000;
/** How far behind the chain the market feed may fall before we warn. */
const MARKET_STALENESS_THRESHOLD_MS = 40_000;
/** How far *ahead* of our slot feed the market feed may run before we resubscribe. */
const SLOT_CONTEXT_STALENESS_THRESHOLD_MS = 40_000;
const INITIAL_LOAD_GRACE_PERIOD_MS = 5000;
const STALE_NOTIFICATION_ID = 'stale-data-warning';

const showWarningSubject = new BehaviorSubject<boolean>(false);

/**
 * Displays a warning notification if the orderbook websocket data is stale.
 * To determine if the orderbook websocket data is stale, we check the least stale slot of the following data points:
 * - L2 orderbook
 * - Oracle
 * - Market
 * - MM Oracle
 *
 * Since those data points are from the same source (orderbook websocket), we can use that to determine if the
 * orderbook websocket is stale.
 */
export const useDisplayStalePageWarning = () => {
	const { removeNotificationById } = useNotificationStore();
	const resubscribe = useForceSlotResubscribe();
	const selectedMarketId = useVelocityStore((s) => s.selectedMarket.marketId);

	const {
		l2SlotStaleness,
		oracleSlotStaleness,
		marketSlotStaleness,
		mmOracleSlotStaleness,
	} = useTargetMarketSlotStaleness(selectedMarketId);
	const leastStaleSlotStaleness = Math.min(
		l2SlotStaleness,
		oracleSlotStaleness,
		marketSlotStaleness,
		mmOracleSlotStaleness
	);

	// Both thresholds are wall-clock intents measured in slot space, so they
	// convert at the live slot length and floor: a shorter window means the
	// warning fires sooner rather than tolerating stale data.
	const { slotDurationMs } = useRiskCeilingSlotClock();
	const marketStalenessThresholdSlots = msToSlotsNum(
		MARKET_STALENESS_THRESHOLD_MS,
		slotDurationMs
	);
	const slotContextStalenessThresholdSlots = msToSlotsNum(
		SLOT_CONTEXT_STALENESS_THRESHOLD_MS,
		slotDurationMs
	);

	const isMobileScreenSize = useIsMobileScreenSize();

	const [lastActiveTime, setLastActiveTime] = React.useState(Date.now());
	const [isDocumentHidden, setIsDocumentHidden] = React.useState(false);

	// Update lastActiveTime when page becomes visible
	useEffect(() => {
		const handleVisibilityChange = () => {
			if (document.hidden) {
				setIsDocumentHidden(true);
			} else {
				setLastActiveTime(Date.now());
				setIsDocumentHidden(false);
			}
		};

		document.addEventListener('visibilitychange', handleVisibilityChange);

		return () => {
			document.removeEventListener('visibilitychange', handleVisibilityChange);
		};
	}, []);

	const isSlotContextStale =
		leastStaleSlotStaleness < -slotContextStalenessThresholdSlots &&
		lastActiveTime + INITIAL_LOAD_GRACE_PERIOD_MS < Date.now();

	const marketSlotIsStale =
		leastStaleSlotStaleness > marketStalenessThresholdSlots ||
		VelocityWindow.FORCE_DISPLAY_STALE_PAGE_WARNING;
	const isAfterGracePeriod =
		lastActiveTime + INITIAL_LOAD_GRACE_PERIOD_MS < Date.now();
	const description = isMobileScreenSize
		? null
		: "Please refresh the page to ensure you're seeing the most up-to-date data.";

	const showWarningNotification = () => {
		notify({
			id: STALE_NOTIFICATION_ID,
			type: 'info',
			message: 'Page data may be stale.',
			description,
			bottomContent: (
				<Button
					size="sm"
					variant="stroke"
					onClick={() => window.location.reload()}
				>
					Refresh Page
				</Button>
			),
			showUntilCancelled: true,
		});
	};

	useEffect(() => {
		if (isSlotContextStale) {
			resubscribe();
		}
	}, [isSlotContextStale]);

	useEffect(() => {
		const subscription = showWarningSubject
			.pipe(debounceTime(NOTIFICATION_DEBOUNCE_TIME_MS))
			.subscribe((shouldShowWarning) => {
				if (shouldShowWarning) {
					showWarningNotification();
				} else {
					removeNotificationById(STALE_NOTIFICATION_ID);
				}
			});

		return () => {
			subscription.unsubscribe();
		};
	}, [showWarningSubject]);

	useEffect(() => {
		showWarningSubject.next(
			marketSlotIsStale && isAfterGracePeriod && !isDocumentHidden
		);
	}, [marketSlotIsStale, isAfterGracePeriod, isDocumentHidden]);
};
